import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import { ROLE_LABEL } from "@/constants/roles";
import { initials } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import HRMSGovernance from "@/components/admin/HRMSGovernance";
import DataCleanup from "@/components/admin/DataCleanup";
import { toast } from "sonner";
import { Users, ShieldCheck, Cpu, Database, Loader2, RefreshCw, Trash2 } from "lucide-react";

const MODELS = [
  "claude-haiku-4-5-20251001",
  "claude-sonnet-5-5",
  "claude-opus-5-5",
];

export default function Admin() {
  const { can, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [health, setHealth] = useState(null);
  const [aiCfg, setAiCfg] = useState({
    provider: "anthropic",
    model: "claude-haiku-4-5-20251001",
  });
  const [resetOpen, setResetOpen] = useState(false);
  const [working, setWorking] = useState(false);

  const loadAll = () => {
    if (can("admin:manage_users")) api.get("/admin/users").then(({ data }) => setUsers(data.items || [])).catch(() => {});
    api.get("/admin/roles").then(({ data }) => setRoles(data.roles || [])).catch(() => {});
    api.get("/admin/system-health").then(({ data }) => setHealth(data)).catch(() => {});
    api.get("/admin/ai-config").then(({ data }) => setAiCfg(data)).catch(() => {});
  };
  useEffect(loadAll, []); // eslint-disable-line

  const toggleUser = async (uid) => {
    try { const { data } = await api.post(`/admin/users/${uid}/toggle-active`);
      setUsers((us) => us.map((u) => u.id === uid ? { ...u, is_active: data.is_active } : u));
      toast.success("User updated");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveAi = async () => {
    try { await api.put("/admin/ai-config", aiCfg); toast.success("AI model updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const resetDemo = async () => {
    setWorking(true);
    try { const { data } = await api.post("/admin/reset-demo-data"); toast.success("Demo data removed"); setResetOpen(false); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setWorking(false); }
  };
  const reseed = async () => {
    setWorking(true);
    try { await api.post("/admin/reseed-demo-data"); toast.success("Demo data reseeded"); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setWorking(false); }
  };

  return (
    <div>
      <PageHeader title="Administration" subtitle="Users, roles, audit, AI settings and system controls" />
      <Tabs defaultValue="users">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="h-4 w-4 mr-1.5" />Users</TabsTrigger>
          <TabsTrigger value="roles" data-testid="admin-tab-roles"><ShieldCheck className="h-4 w-4 mr-1.5" />Roles</TabsTrigger>
          {user?.role === "super_admin" && (
            <TabsTrigger value="hrms-governance" data-testid="admin-tab-hrms-governance">
              <ShieldCheck className="h-4 w-4 mr-1.5" /> HRMS governance
            </TabsTrigger>
          )}
          <TabsTrigger value="ai" data-testid="admin-tab-ai"><Cpu className="h-4 w-4 mr-1.5" />AI</TabsTrigger>
          <TabsTrigger value="system" data-testid="admin-tab-system"><Database className="h-4 w-4 mr-1.5" />System</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin">
              <Table>
                <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Region</TableHead><TableHead>Active</TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell><div className="flex items-center gap-3"><Avatar className="h-8 w-8"><AvatarFallback className="bg-[#255058] text-white text-xs">{initials(u.name)}</AvatarFallback></Avatar><div><div className="font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></div></div></TableCell>
                      <TableCell><Badge variant="secondary">{ROLE_LABEL[u.role] || u.role}</Badge></TableCell>
                      <TableCell>{u.region}</TableCell>
                      <TableCell><Switch checked={u.is_active !== false} onCheckedChange={() => toggleUser(u.id)} data-testid={`user-toggle-${u.id}`} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {roles.map((r) => (
              <Card key={r.key} className="p-4">
                <div className="font-medium">{r.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{r.permissions.includes("*") ? "Full access (all permissions)" : `${r.permissions.length} permissions`}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(r.permissions.includes("*") ? ["*"] : r.permissions.slice(0, 6)).map((p) => <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>)}
                  {!r.permissions.includes("*") && r.permissions.length > 6 && <Badge variant="outline" className="text-[10px]">+{r.permissions.length - 6}</Badge>}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {user?.role === "super_admin" && (
          <TabsContent value="hrms-governance" className="mt-4">
            <HRMSGovernance />
          </TabsContent>
        )}

        <TabsContent value="ai" className="mt-4">
          <Card className="p-5 max-w-lg">
            <div className="font-display font-semibold mb-1">AI Model Configuration</div>
            <p className="text-sm text-muted-foreground mb-4">The Claude model used by NEXREQ Intelligence and Nexreq AI Workspace.</p>
            <div className="flex gap-3">
              <Select value={aiCfg.model} onValueChange={(v) => setAiCfg((c) => ({ ...c, model: v, provider: "anthropic" }))}>
                <SelectTrigger data-testid="admin-ai-model"><SelectValue /></SelectTrigger>
                <SelectContent>{MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={saveAi} data-testid="admin-ai-save" className="bg-[#255058] hover:bg-[#1c3d43] text-white">Save</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="system" className="mt-4 space-y-4">
          {health && (
            <Card className="p-5">
              <div className="font-display font-semibold mb-3">System Health · <span className="text-[#2f9e5f]">{health.status}</span></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(health.collections).map(([k, v]) => (
                  <div key={k} className="p-3 rounded-lg border border-border"><div className="text-xs text-muted-foreground capitalize">{k}</div><div className="font-display text-xl font-bold">{v}</div></div>
                ))}
              </div>
            </Card>
          )}
          {can("admin:reset_demo") && (
            <Card className="p-5 border-amber-300">
              <div className="font-display font-semibold mb-1">Demo Data Controls</div>
              <p className="text-sm text-muted-foreground mb-4">Demo records are clearly flagged and fully removable. The platform works after removal.</p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={reseed} disabled={working} data-testid="admin-reseed-demo" className="gap-1.5">{working ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Reseed Demo Data</Button>
                <Button variant="outline" onClick={() => setResetOpen(true)} className="gap-1.5 text-red-600" data-testid="admin-reset-demo"><Trash2 className="h-4 w-4" /> Remove All Demo Data</Button>
              </div>
            </Card>
          )}
          {user?.role === "super_admin" && <DataCleanup />}
        </TabsContent>
      </Tabs>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Remove all demo data?</DialogTitle><DialogDescription>This deletes every record flagged as demo (candidates, clients, jobs, submissions, placements, invoices, demo users). This action is logged.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={resetDemo} disabled={working} data-testid="admin-reset-confirm">{working ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove Demo Data"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
